import { useState } from "react";
import PropTypes from "prop-types";
import Dialog from "../../dialogs/Dialog";
import Button from "../../buttons/Button";
import DropDownList from "../../forms/DropDownList";
import SaveFilter from "./SaveFilterDialog";
import ConfirmDialog from "../../dialogs/ConfirmDialog";
import "./DialogFilter.scss";
import { buildUrl, addTextField, getHistoryFilter, removeFormFields, isDateType, removeDisabledPropertyFromArrayItems } from "../../utils/helper";
import { dateToStr } from "../../utils/convert";
import Checkbox from "../../forms/Checkbox";
import sk from "../../localization/sk.json";
import Input from "../../forms/Input";
import DateInput from "../../forms/DateInput";
import NumericTextBox from "../../forms/NumericTextBox";
import ComboBox from "../../forms/ComboBox";
import DateRange from "../../forms/DateRangeInput";
import LoadingPanel from "../../LoadingPanel";
import _ from "lodash";
import { useDidMount } from "../../utils/hooks";
import { getUser } from "../../utils/storage";

export const NUMERIC_OPS = [
  { text: sk.grid.filterEqOperator, operator: "eq" },
  { text: sk.grid.filterNotEqOperator, operator: "neq" },
  { text: sk.grid.filterGteOperator, operator: "gte" },
  { text: sk.grid.filterGtOperator, operator: "gt" },
  { text: sk.grid.filterLteOperator, operator: "lte" },
  { text: sk.grid.filterLtOperator, operator: "lt" },
  { text: sk.grid.filterIsNullOperator, operator: "isnull" },
  { text: sk.grid.filterIsNotNullOperator, operator: "isnotnull" },
  { text: sk.grid.filterIsInListOperator, operator: "in" },
  { text: sk.grid.filterIsNotInListOperator, operator: "notin" }
];

const TEXT_OPS = [
  { text: sk.grid.filterContainsOperator, operator: "contains" },
  { text: sk.grid.filterNotContainsOperator, operator: "doesnotcontain" },
  { text: sk.grid.filterEqOperator, operator: "eq" },
  { text: sk.grid.filterNotEqOperator, operator: "neq" },
  { text: sk.grid.filterStartsWithOperator, operator: "startswith" },
  { text: sk.grid.filterEndsWithOperator, operator: "endswith" },
  { text: sk.grid.filterIsEmptyOperator, operator: "isempty" },
  { text: sk.grid.filterIsNotEmptyOperator, operator: "isnotempty" },
];

const DATE_OPS = [
  { text: sk.grid.filterEqOperator, operator: "eq" },
  { text: sk.grid.filterNotEqOperator, operator: "neq" },
  { text: sk.grid.filterAfterOrEqualOperator, operator: "gte" },
  { text: sk.grid.filterAfterOperator, operator: "gt" },
  { text: sk.grid.filterBeforeOperator, operator: "lt" },
  { text: sk.grid.filterBeforeOrEqualOperator, operator: "lte" },
  { text: sk.grid.filterIsNullOperator, operator: "isnull" },
  { text: sk.grid.filterIsNotNullOperator, operator: "isnotnull" },
  { text: sk.grid.filterBetweenOperator, operator: "between" },
];

export const FILTER_OPERATORS = {
  text: TEXT_OPS,
  string: TEXT_OPS,
  numeric: NUMERIC_OPS,
  unitnumeric: NUMERIC_OPS,
  date: DATE_OPS,
  dateinput: DATE_OPS,
  datetime: DATE_OPS,
  datetimeinput: DATE_OPS,
  container: TEXT_OPS,
  boolean: [
    { text: sk.grid.filterEqOperator, operator: "eq" },
    { text: sk.grid.filterNotEqOperator, operator: "neq" },
  ],
  enum: [
    { text: sk.grid.filterEqOperator, operator: "eq" },
    { text: sk.grid.filterNotEqOperator, operator: "neq" },
  ],
  buttongroup: [
    { text: sk.grid.filterEqOperator, operator: "eq" },
    { text: sk.grid.filterNotEqOperator, operator: "neq" },
  ],
  radio: [
    { text: sk.grid.filterEqOperator, operator: "eq" },
    { text: sk.grid.filterNotEqOperator, operator: "neq" },
  ],
  cmd: [
    { text: sk.grid.filterContainsOperator, operator: "contains" },
    { text: sk.grid.filterNotContainsOperator, operator: "doesnotcontain" },
    { text: sk.grid.filterEqOperator, operator: "eq" },
    { text: sk.grid.filterNotEqOperator, operator: "neq" },
  ],
};

const _boolean = [
  { text: sk.grid.filterBooleanAll, operator: null },
  { text: sk.grid.filterIsTrue, operator: true },
  { text: sk.grid.filterIsFalse, operator: false },
];
const _join = [
  { value: false, text: "súčasne" },
  { value: true, text: "alebo" },
];
const _left = [" - ", "(", "((", "(((", "((((", "(((((", "(((((("];
const _right = [" - ", ")", "))", ")))", "))))", ")))))", "))))))"];

/**
 * Filter in dialog
 * @module
 * @param {array} schema - Schema
 * @param {object} [parentProps] - Parent props
 * @param {bool} isOpen - Is dialog open?
 * @param {func} handleOpenFilter - Function to open filter
 * @param {func} handleCloseFilter - Functions to close filter
 * @param {object} filter - Filter
 * @param {string} enumDefault - Default value in enum
 * @param {sting} primaryField - Primary field
 * @param {bool} userFilter - User filter
 * @param {bool} userFilterDisabled - User filter is disabled
 * @param {array} [userFilterList] - List of user filters
 * @param {func} getSchema - Get schema
 * @param {func} getDefaultUserId - Get default user ID
 * @param {string} [db] - db
 */
const DialogFilterExtended = (props) => {
  let alertTimeout;

  const [isOpenSaveDialog, setIsOpenSaveDialog] = useState(false);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [selectFilter, setSelectFilter] = useState(null);
  const [filter, setFilter] = useState(props.filter?.filters || []);
  const [startFilter, setStartFilter] = useState(false);
  const [error, setError] = useState(null);
  const [alertMsg, setAlertMsg] = useState(null);
  const [nameError, setNameError] = useState(null);
  const [filterId, setFilterId] = useState(0);
  const [history, setHistory] = useState(0);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const buildFilterExtended = (input) => {
    let newFilters = [];
    for (let x of input) {
      if (x.item && x.item.type === "date" && x.value && x.value instanceof Date) {
        x.value.setHours(0, 0, 0, 0);
      }
      const joins = x.form?.Joins.map(join => {
        return {
          childField: join.Field,
          parentField: join.ParentField,
          operator: join.Operator,
          childStl: join.ChildFieldStl,
          childId: join.ChildFieldId
        };
      });
      const field = x.item ? ((x.item.filter === "container" && !x.form?.Id) ? x.item.field + "." + x.item.textField : x.item.field) : null;
      let newFilterItem = {
        field: field,
        operator: x.operator,
        value: x.value !== undefined && x.value !== null ? x.value : null,
        join: x.join || false,
        left: x.left && x.left.includes("(") ? x.left : null,
        right: x.right && x.right.includes(")") ? x.right : null,
        order: x.id,
        frmPod: x.form?.Id || null,
        filterId: x.filterId,
        filterType: x.filterType,
        joins: joins
      };
      newFilters = [...newFilters, newFilterItem];
    }
    const newFilter = {
      filters: [...newFilters],
    };
    return newFilter;
  };

  const getOperator = (operator, filter) => {
    if (_.isObject(operator)) return operator;

    switch (operator) {
      case "custom":
        return { text: "definovaný filter", operator: "custom" };
      case "exists":
        return { text: "existuje záznam", operator: "exists" };
      case "notexists":
        return { text: "neexistuje záznam", operator: "notexists" };
      default:
        return FILTER_OPERATORS[filter && filter !== "none" ? filter : "text"].find((el) => el.operator === operator);
    }
  };

  const prepareDateValue = (x, item) => {
    // date value from string
    let value = x.value;
    if (item && isDateType(item.type) && x.operator !== "between" && typeof value === "string") {
      value = new Date(value);
    }
    if (item && isDateType(item.type) && x.operator === "between" && typeof value === "object" && value.start && typeof value.start === "string") {
      value = { start: new Date(value.start), end: new Date(value.end) };
    }
    return value;
  };

  const prepareNewFilters = async (filters) => {
    if (!filters || filters?.length === 0) return [];
    let newFilters = [];

    setIsLoading(true);
    await Promise.all(
      filters.map(async (x, index) => {
        let form = props.frms.find((el) => el.Id === x.frmPod) || null;
        let schema = props.schema;

        if (form) {
          let getSchema = await props.getSchema(form.Identif, props.db);
          schema = getSchema.Grid;
        }
        let item = schema.find((el) => el.field === x.field || el.field + "." + el.textField === x.field || el.field + "." + el.primaryField === x.field);
        let id = index;

        const value = prepareDateValue(x, item);
        const join = x.join || false;
        const left = x.left && x.left.includes("(") ? x.left : null;
        const right = x.right && x.right.includes(")") ? x.right : null;

        newFilters = [...newFilters, { ...x, id, item, form, schema, value, join, left, right }];
        return x;
      })
    );
    setIsLoading(false);
    return newFilters;
  };

  const prepareFilter = async (filters) => {
    const newFilters = await prepareNewFilters(filters);

    let filterId = newFilters.length > 0 ? Math.max(...newFilters.map((el) => el.id)) + 1 : 0;
    setFilter(newFilters);
    setFilterId(filterId);
  };

  const compareUserFilter = (filters) => {
    const comparisonFilter = removeFormFields(filters);
    if (filters.length > 0) {
      for (let userFilter of props.userFilterList) {
        const comparisonUserFilter = removeFormFields(userFilter.filters);
        if (_.xorWith(comparisonFilter, comparisonUserFilter, _.isEqual).length === 0) {
          return userFilter;
        }
      }
    }
    return null;
  };

  const prepareUserFilter = (filters) => {
    const selectFilter = compareUserFilter(filters);
    if (selectFilter) {
      handleChangeUserFilter("selectFilter", selectFilter);
    } else {
      setSelectFilter(null);
      setStartFilter(false);
    }
  };

  const setDefaultUser = async () => {
    const iUz = props.getDefaultUserId ? (await props.getDefaultUserId()) : getUser()?.I_UZ;
    if (iUz !== user) {
      setUser(iUz);
      setSelectFilter(null);
      setStartFilter(false);
      await props.getFilterList(iUz);
    }
  };

  const init = async () => {
    await setDefaultUser();

    await prepareFilter(props.filter);
    prepareUserFilter(props.filter);

    if (props.filter?.length === 0) {
      addNewFilter();
    }
  };

  useDidMount(() => {
    init();
  });

  const setAlert = (msg, isError) => {
    setAlertMsg(msg);
    setError(isError);
    clearTimeout(alertTimeout);
    alertTimeout = setTimeout(() => {
      setAlertMsg(null);
      setError(null);
    }, 4500);
  };

  const handleDeleteFilter = () => {
    if (selectFilter) {
      try {
        props.deleteFilter(selectFilter.id, user);
        setFilter([]);
        setSelectFilter(null);
        setStartFilter(false);
        setAlert("Filter úspešne vymazaný", false);
      } catch (error) {
        setAlert("Nepodarilo sa vymazať filter", true);
      }
    }
  };

  const handleChangeUserFilter = (name, value) => {
    const filter = { filters: addTextField(value.filters, props.schema) };
    prepareFilter(filter.filters);

    setSelectFilter(value);
    const userFilter = props.userFilterList.find(x => x.id === value.id);
    setStartFilter(userFilter?.disabled === undefined || userFilter?.disabled === null ? false : !userFilter.disabled);
  };

  const setHistoryFilter = async (history) => {
    await setDefaultUser();
    let allFilters = getHistoryFilter(props.primaryField);
    let filters = allFilters?.[history] || [];
    prepareFilter(filters);
    prepareUserFilter(filters);
    setHistory(history);
  };

  const next = () => {
    setHistoryFilter(history - 1);
  };

  const disabledNext = () => {
    return history === 0;
  };

  const previous = async () => {
    let newHistory = history;
    if (newHistory === 0) {
      const oldFilter = getHistoryFilter(props.primaryField);
      const oldFilters = await prepareNewFilters(oldFilter[0]);
      const value = buildFilterExtended(filter);
      if (_.xorWith(removeFormFields(value.filters), removeFormFields(oldFilters), _.isEqual).length !== 0) {
        newHistory = 0;
      } else {
        newHistory = 1;
      }
    } else {
      newHistory++;
    }
    setHistoryFilter(newHistory);
  };

  const disabledPrevious = () => {
    const historyFilter = getHistoryFilter(props.primaryField);
    return historyFilter.length <= history + 1 || historyFilter.length === 0;
  };

  const handleSaveFilter = async (name) => {
    if (props.userFilterList.find((el) => el.name === name)) {
      setNameError("Filter s týmto menom už existuje.");
      return;
    }
    try {
      const newFilter = buildFilterExtended(filter);
      let newFilterList = await props.createFilter({ name, filters: newFilter.filters, disabled: true }, user);
      let selectFilter = newFilterList.find((el) => el.name === name);
      handleChangeUserFilter("selectFilter", selectFilter);
      handleCloseSaveDialog();
      setAlert("Filter úspešne vytvorený", false);
    } catch (error) {
      setAlert("Nepodarilo sa uložiť filter", true);
    }
  };

  const handleUpdateFilter = async () => {
    try {
      props.deleteFilter(selectFilter.id, user);
      const name = selectFilter.name;
      const newFilter = buildFilterExtended(filter);
      let newFilterList = await props.createFilter(
        {
          name,
          filters: newFilter.filters,
          disabled: !startFilter,
        },
        user
      );
      handleChangeUserFilter(
        "selectFilter",
        newFilterList.find((el) => el.name === name)
      );
      setAlert("Filter úspešne upravený", false);
    } catch (error) {
      setAlert("Nepodarilo sa uložiť filter", true);
    }
  };

  const handleOpenSaveDialog = () => {
    setIsOpenSaveDialog(true);
  };

  const handleCloseSaveDialog = () => {
    setIsOpenSaveDialog(false);
    setNameError(null);
  };

  const validateFilters = (filters) => {
    let bracketCount = 0;
    for (let i = 0; i < filters.length; i++) {
      const filter = filters[i];
      if (filter.join === true && bracketCount < 1) {
        return `Riadok ${i + 1}: Operátor "alebo" musí byť v rámci zátvorky.`;
      }

      const leftCount = filter.left ? (filter.left.match(/\(/g) || []).length : 0;
      const rightCount = filter.right ? (filter.right.match(/\)/g) || []).length : 0;
      bracketCount += leftCount - rightCount;
      if (bracketCount < 0) {
        return `Riadok ${i + 1}: Zátvorka je uzatvorená skôr, než bola otvorená.`;
      }
    }
    if (bracketCount !== 0) {
      return "Niektoré zátvorky neboli uzatvorené.";
    }
    return null;
  };

  const handleConfirmFilter = async () => {
    let newFilter = buildFilterExtended(filter);

    const validationError = validateFilters(newFilter.filters);
    if (validationError) {
      setAlert(validationError, true);
      return;
    }

    if (selectFilter) {
      const newSelectedFilter = compareUserFilter(newFilter.filters)
      if (newSelectedFilter) {
        newFilter = newSelectedFilter;
      }
    }
    props.handleConfirmFilter(newFilter);
  };

  const handleOpenDelete = () => {
    setIsOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setIsOpenDelete(false);
  };

  const handleConfirmDelete = () => {
    handleDeleteFilter();
    handleCloseDelete();
  };

  const handleStartFilter = (name, value) => {
    try {
      setStartFilter(value);
      props.updateFilter(selectFilter.id, !value, selectFilter.type, user);
      const msg = value ? "Štartovací filter bol zapnutý" : "Štartovací filter bol vypnutý";
      setAlert(msg, false);
    } catch (error) {
      setAlert("Nepodarilo sa nastaviť štartovací filter", true);
    }
  };

  const addNewFilter = () => {
    setFilter((prev) => [...prev, { id: filterId }]);
    setFilterId((prev) => prev + 1);
  };

  const removeFilter = (item) => {
    let newFilter = filter.filter((el) => el.id !== item.id);
    newFilter = unselectFilter(newFilter, item.filterId);
    setFilter(newFilter);
  };

  const unselectFilter = (filter, filterId) => {
    if (!filterId) return filter;
    return filter.map((el) => el.filterId === filterId ? { ...el, filterId: null } : el);
  };

  const handleChange = async (name, value, id) => {
    let filterId;
    let newFilter = await Promise.all(
      filter.map(async (item) => {
        if (item.id === id) {
          item[name] = ["left", "right"].includes(name) ? value.text : name === "operator" ? value.operator : value;
          filterId = item.filterId;

          if (name === "form") {
            if (value.Identif) {
              setIsLoading(true);
              const schema = await props.getSchema(value.Identif, props.db);
              item.schema = schema.Grid;
              setIsLoading(false);
            } else {
              item.schema = props.schema;
            }

            item.item = null;
            item.operator = null;
            item.value = null;
          } else if (name === "item") {
            item.operator = FILTER_OPERATORS[value.filter && value.filter !== "none" && value.filter !== "string" ? value.filter : "text"][0]?.operator;
            item.value = null;
          } else if (name === "operator" && ["isnull", "isnotnull", "isempty", "isnotempty"].includes(value.operator)) {
            item.value = null;
          } else if (name === "operator" && ["exists", "notexists"].includes(value.operator)) {
            item.value = null;
            item.item = null;
          }
        }
        return item;
      })
    );

    newFilter = unselectFilter(newFilter, filterId);
    setFilter(newFilter);
  };

  const handleChangeUser = async (name, value) => {
    if (value) {
      setUser(value.IUz);
      setSelectFilter(null);
      setStartFilter(false);
      props.getFilterList(value.IUz);
    } else {
      setUser(null);
      setSelectFilter(null);
      setStartFilter(false);
    }
  };

  const onBlurUser = () => {
    if (!user) {
      setDefaultUser();
    }
  };

  const orderSchema = (schema) => {
    return _.sortBy(
      schema.filter((x) => x.filterable),
      "orderFilter"
    );
  };

  const hasValue = (value) => {
    return value && value !== props.enumDefault;
  };

  const getValueInput = (dataItem, type) => {
    const operator = dataItem?.operator;
    const customType = operator === "custom" ? "container" : type;
    switch (customType) {
      case "enum":
      case "buttongroup":
      case "radio":
        const primaryField = dataItem.item.primaryField;
        const data = (props.parentProps[dataItem.item.dataField] || dataItem.item.enumData).filter(x => x[primaryField] !== null);
        const filterItem = filter.find(x => x.id === dataItem.id);
        const enumValue = primaryField ? data.find(x => x[primaryField] === Number(filterItem?.value)) : filter.find((x) => x.id === dataItem.id)?.value;

        return (
          <div className="filter-input value">
            <DropDownList
              name={"value"}
              data={data}
              onChange={(name, value) => {
                handleChange(name, hasValue(value) ? (primaryField ? value[primaryField] : value) : "", dataItem.id);
              }}
              value={enumValue || props.enumDefault}
              textField={dataItem.item.textField}
              defaultItem={props.enumDefault}
              disabled={["custom", "isnull", "isnotnull"].includes(operator)}
            />
          </div>
        );
      case "boolean":
        return (
          <div className="filter-input value">
            <DropDownList name={"value"} data={_boolean} onChange={(name, value) => handleChange(name, value.operator, dataItem.id)} value={_boolean.find((el) => el.operator === dataItem.value)} textField="text" disabled={["custom", "isnull", "isnotnull"].includes(operator)} />
          </div>
        );
      case "date":
      case "datetime":
      case "dateinput":
      case "datetimeinput":
        return (
          <div className="filter-input value">
            {operator === "between" && (
              <DateRange
                name={"value"}
                onChange={(name, value) => handleChange(name, value, dataItem.id)}
                value={dataItem.value}
                calendarSettings={{
                  bottomView: "month",
                  topView: "year",
                  views: 3,
                }}
                format={{
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                }}
                disabled={["custom", "isnull", "isnotnull"].includes(operator)}
              />
            )}
            {operator !== "between" && (
              <DateInput name={"value"} onChange={(name, value) => handleChange(name, value, dataItem.id)} value={dataItem.value instanceof Date ? dataItem.value : null} disabled={["custom", "isnull", "isnotnull"].includes(operator)} type={"date"} validationMessage={" "} />
            )}
          </div>
        );
      case "numeric":
      case "unitnumeric":
        if (operator === "in" || operator === "notin") {
          return (
            <div className="filter-input value">
              <Input name={"value"} onChange={(name, value) => handleChange(name, value, dataItem.id)} value={_.toString(dataItem.value)} disabled={["custom", "isnull", "isnotnull"].includes(operator)} />
            </div>
          );
        }
        else {
          return (
            <div className="filter-input value">
              <NumericTextBox name={"value"} onChange={(name, value) => handleChange(name, value, dataItem.id)} value={dataItem.value} disabled={["custom", "isnull", "isnotnull"].includes(operator)} />
            </div>
          );
        }
      case "container":
      default:
        return (
          <div className="filter-input value">
            <Input name={"value"} onChange={(name, value) => handleChange(name, value, dataItem.id)} value={_.toString(dataItem.value)} disabled={["custom", "isnull", "isnotnull"].includes(operator)} />
          </div>
        );
    }
  };

  const buildFilterRow = () => {
    return (
      <>
        {filter.map((dataItem, index) => {
          let operators = FILTER_OPERATORS[dataItem.item?.type] || FILTER_OPERATORS.text;
          if (dataItem.form) {
            operators = [...operators, { text: "existuje záznam", operator: "exists" }, { text: "neexistuje záznam", operator: "notexists" }];
          }
          const fields = dataItem.schema ? removeDisabledPropertyFromArrayItems(dataItem.schema) : removeDisabledPropertyFromArrayItems(props.schema);
          const operator = dataItem?.operator;
          const frms = props.frms.filter(frm => frm.CanFilterBySubform);

          return (
            <tr key={index}>
              <td>
                <div className="k-form-field filter-input">
                  <DropDownList name={"join"} data={_join} onChange={(name, value) => handleChange(name, value.value, dataItem.id)} value={_join.find((el) => el.value === dataItem.join) || _join[0]} textField="text" />
                </div>
              </td>
              <td className={"left"}>
                <div className="k-form-field filter-input">
                  <DropDownList name={"left"} data={_left.map((x) => ({ text: x }))} onChange={(name, value) => handleChange(name, value, dataItem.id)} value={{ text: dataItem.left || "" }} textField="text" />
                </div>
              </td>
              <td>
                <div className="k-form-field filter-input">
                  <DropDownList name="form" data={frms} onChange={(name, value) => handleChange(name, value, dataItem.id)} value={dataItem.form} textField="Name" defaultItem={{ Name: " - " }} disabled={frms.length === 0} />
                </div>
              </td>
              <td>
                <div className="k-form-field filter-input">
                  <DropDownList name="item" data={orderSchema(fields)} onChange={(name, value) => handleChange(name, value, dataItem.id)} value={dataItem.item} textField="filterTitle" disabled={operator === "custom"} />
                </div>
              </td>
              <td>
                <div className="k-form-field filter-input">
                  <DropDownList name={"operator"} data={operators} onChange={(name, value) => handleChange(name, value, dataItem.id)} value={getOperator(operator, dataItem.item?.type)} textField="text" disabled={operator === "custom"} />
                </div>
              </td>
              <td className="filter-value">
                <div className="k-form-field filter-input">{getValueInput(dataItem, dataItem.item?.type)}</div>
              </td>
              <td className={"right"}>
                <div className="k-form-field filter-input">
                  <DropDownList name={"right"} data={_right.map((x) => ({ text: x }))} onChange={(name, value) => handleChange(name, value, dataItem.id)} value={{ text: dataItem.right || "" }} textField="text" />
                </div>
              </td>
              <td className={"dialog-filter-close"}>
                <div className="k-form-field filter-input">
                  <Button onClick={() => removeFilter(dataItem)} icon="close" />
                </div>
              </td>
            </tr>
          );
        })}
      </>
    );
  };


  const editableFilter = props.userFilterDisabled || !selectFilter?.id || selectFilter?.type !== 3;
  const startableFilter = props.userFilterDisabled || !selectFilter?.id || !selectFilter?.CanDisabled;
  const userFilterList = removeDisabledPropertyFromArrayItems(props.userFilterList);

  return (
    <>
      <Dialog isOpen={props.isOpen} onClose={props.handleCloseFilter} onConfirm={handleConfirmFilter} title="Pokročilý filter" width={1260} disableConfirmOnEnter>
        <div>
          {props.userFilter && (
            <div className="toolbar filter-toolbar">
              <div className="left">
                <Button disabled={props.userFilterDisabled} onClick={handleOpenSaveDialog} title="Nový filter" imageUrl={buildUrl("assets/icons/toolbar/new.svg")} />
                <Button disabled={editableFilter} onClick={handleUpdateFilter} title="Uložiť filter" imageUrl={buildUrl("assets/icons/toolbar/save.svg")} />
                <Button disabled={editableFilter} onClick={handleOpenDelete} title="Zmazať filter" imageUrl={buildUrl("assets/icons/toolbar/delete.svg")} />
                <Button onClick={previous} imageUrl={buildUrl("assets/icons/toolbar/posun-vlavo.svg")} title="Predchádzajúci filter" disabled={disabledPrevious()} />
                <Button onClick={next} imageUrl={buildUrl("assets/icons/toolbar/posun-vpravo.svg")} title="Ďalší filter" disabled={disabledNext()} />
              </div>
              <Checkbox name="Start" label="Štartovací filter" onChange={handleStartFilter} value={startFilter} disabled={startableFilter} />
              <div className="right">
                <ComboBox disabled={props.userFilterDisabled} name={"user"} data={props.managedUsers || []} textField="NUz" onChange={handleChangeUser} value={props.managedUsers?.find((el) => el.IUz === user)} onBlur={onBlurUser} />
                <DropDownList disabled={props.userFilterDisabled} name={"selectFilter"} data={userFilterList} textField="name" onChange={handleChangeUserFilter} value={selectFilter || { name: " - ", filters: [] }} defaultItem={{ name: " - ", filters: [] }} />
              </div>
            </div>
          )}
          {alertMsg && (
            <div className={"filter-alert mb-0 " + (error ? "error" : "success")}>
              <span className="alert-text">{alertMsg}</span>
            </div>
          )}

          <div className="k-form-inline dialogFilterExtended">
            <LoadingPanel isLoading={isLoading} />

            {filter?.length > 0 && (
              <table>
                <thead>
                  <tr>
                    <th>logika</th>
                    <th>ľavá zátvorka</th>
                    <th>formulár</th>
                    <th>stĺpec</th>
                    <th>operátor</th>
                    <th>hodnota</th>
                    <th>pravá zátvorka</th>
                    <th />
                  </tr>
                </thead>
                <tbody>{buildFilterRow()}</tbody>
              </table>
            )}
            <div>
              <Button buttonWrapperClassName="addButtonWrapper" onClick={addNewFilter} icon="add" />
            </div>
          </div>
        </div>
      </Dialog>
      <SaveFilter isOpen={isOpenSaveDialog} handleClose={handleCloseSaveDialog} handleConfirm={handleSaveFilter} error={nameError} />
      <ConfirmDialog isOpen={isOpenDelete} onClose={handleCloseDelete} onConfirm={handleConfirmDelete} text={`Zmazať filter "${selectFilter?.name}"?`} />
    </>
  );
};

DialogFilterExtended.propTypes = {
  schema: PropTypes.array.isRequired,
  parentProps: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  handleOpenFilter: PropTypes.func.isRequired,
  handleCloseFilter: PropTypes.func.isRequired,
  handleConfirmFilter: PropTypes.func.isRequired,
  filter: PropTypes.object.isRequired,
  enumDefault: PropTypes.string.isRequired,
  primaryField: PropTypes.string.isRequired,
  userFilter: PropTypes.bool.isRequired,
  userFilterList: PropTypes.array,
  userFilterDisabled: PropTypes.bool.isRequired,
  updateFilter: PropTypes.func,
  getSchema: PropTypes.func,
  db: PropTypes.string,
};

export default DialogFilterExtended;
